import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import {
  Text, StyleSheet, Animated, View, Pressable, Keyboard, Dimensions, ActivityIndicator,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import Button from '../Button';
import DownArrow24 from '../24pxIcons/DownArrow24';
import DateComponent from '../DateComponent';
import isMobile from '../IsMobile';
import Style from '../../constants/Style';
import AlertModal from '../AlertModal';
import { DateRange, DateSelectionRange } from '../../redux/dateRangeSlice';
import { logEvent } from '../LogEvent';
import {
  NotificationSeverity, NotificationType, TicketPriority, TicketStatus,
} from '../../constants/Types';
import PressableStyled, { PressableState } from '../PressableStyled';
import { isValidValue } from '../../constants/Utils';
import ResetFiltersButton from '../ResetFiltersButton';

interface FilterModalProps {
  loading: boolean,
  filterShown: boolean,
  hideFilter: () => void,
  applyFilter,
  dateFilter: DateRange,
  setDateFilter: Dispatch<SetStateAction<DateRange>>,
  priorityFilter: TicketPriority,
  setPriorityFilter: Dispatch<SetStateAction<TicketPriority>>,
  severityFilter: NotificationSeverity,
  setSeverityFilter: Dispatch<SetStateAction<NotificationSeverity>>,
  notificationTypeFilter: NotificationType,
  setNotificationTypeFilter: Dispatch<SetStateAction<NotificationType>>,
  progressionFilter: TicketStatus,
  setProgressionFilter: Dispatch<SetStateAction<TicketStatus>>,
  filterAlertTitle: string,
  filterAlertMessage: string,
  filterAlertVisible: boolean,
  filterAlertToggle: Dispatch<SetStateAction<boolean>>,
  slaNames: string[],
}

const width: number = 526;

const priorityValues: number[] = [
  null, TicketPriority.CRITICAL, TicketPriority.HIGH, TicketPriority.MODERATE, TicketPriority.LOW,
  TicketPriority.OTHER,
];

const severityValues: number[] = [null, NotificationSeverity.CRITICAL, NotificationSeverity.HIGH,
  NotificationSeverity.MODERATE, NotificationSeverity.LOW];

export const severityTitles: string[] = [
  'All', 'S1 - Critical', 'S2 - High', 'S3 - Moderate', 'S4 - Low',
];

export const notificationTypeTitles = {
  [NotificationType.UPDATE]: 'Ticket update',
  [NotificationType.RAISED]: 'Ticket raised',
  [NotificationType.ANOMALY]: 'Anomaly',
  [NotificationType.ALERT]: 'Alert',
  [NotificationType.INSIGHT]: 'Insight',
};

const notificationTypes: NotificationType[] = [
  null, NotificationType.UPDATE, NotificationType.RAISED, NotificationType.ANOMALY,
  NotificationType.ALERT, NotificationType.INSIGHT,
];

const notificationTypeTestIDs: string[] = [
  null, 'ticket-update-type', 'ticket-raised-type', 'anomaly-type', 'alert-type', 'insight-type',
];

export const progressionTitles = {
  [TicketStatus.REPORTED]: 'Reported',
  [TicketStatus.INPROGRESS]: 'In-progress',
  [TicketStatus.COMPLETED]: 'Completed',
  [TicketStatus.REOPENED]: 'Reopened',
};

const progressionValues: TicketStatus[] = [
  null, TicketStatus.REPORTED, TicketStatus.INPROGRESS, TicketStatus.COMPLETED,
  TicketStatus.REOPENED,
];

const progressionTestIDs: string[] = [
  null, 'reported-progression', 'in-progress-progression', 'completed-progression',
  'reopened-progression',
];

const NotificationFilter = ({
  filterShown, hideFilter, applyFilter, dateFilter, priorityFilter, loading, notificationTypeFilter,
  progressionFilter, setDateFilter, setPriorityFilter, setSeverityFilter, setNotificationTypeFilter,
  setProgressionFilter, filterAlertTitle, filterAlertMessage, filterAlertVisible, filterAlertToggle,
  slaNames, severityFilter,
}: FilterModalProps) => {
  const happyEnabled: boolean = useSelector((state: { happyEnabled: { enabled: boolean } }) => state
    .happyEnabled.enabled);
  const [filterDrawerInset] = useState(new Animated.Value(width));
  const [listShownIndex, setListShownIndex] = useState<number>(-1);
  const [selectedRange, setSelectedRange] = useState<DateSelectionRange>(1);
  const insets = useSafeAreaInsets();

  useEffect(() => {
    if (filterShown) {
      Keyboard.dismiss();
      Animated.timing(filterDrawerInset, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(filterDrawerInset, {
        toValue: width,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  }, [filterShown]);

  const resetFilters = () => {
    setPriorityFilter(null);
    setSeverityFilter(null);
    setNotificationTypeFilter(null);
    setProgressionFilter(null);
    setDateFilter({ start: null, end: null });
  };

  return <Animated.View
    style={[
      styles.container,
      isMobile ? { paddingBottom: insets.bottom }
        : { transform: [{ translateX: filterDrawerInset }] },
    ]}
  // contentContainerStyle={[isMobile && ]}
  >
    <Text style={[styles.title, isMobile && { marginTop: 24 }]}>{'Filter'}</Text>
    <Text style={styles.label}>{'Date'}</Text>
    <View style={styles.datePicker}>
      {filterShown && <DateComponent
        onChange={(start: string, end: string) => setDateFilter({ start, end })}
        onCompareChange={() => { }}
        onConfirm={() => { }}
        onOpen={() => setListShownIndex(0)}
        compareEnabled={false}
        startDate={dateFilter?.start}
        endDate={dateFilter?.end}
        renderLeft={!isMobile}
        {...{ selectedRange, setSelectedRange }}
      />}
    </View>
    <View style={Style.row}>
      <Text
        style={styles.label}
        testID='filter-label'
      >
        {'Notification type'}
      </Text>
      {!!notificationTypeFilter && <PressableStyled onPress={() => setNotificationTypeFilter(null)}>
        <Text style={styles.resetFiltersText}>{'RESET'}</Text>
      </PressableStyled>}
    </View>
    <Pressable
      style={styles.dropdownButton}
      onPress={() => {
        logEvent('NotificationFilter', 'type_filter');
        setListShownIndex(listShownIndex === 3 ? -1 : 3);
      }}
      testID='notifications-type-filter-btn'
    >
      <Text style={styles.value}>
        {notificationTypeFilter ? notificationTypeTitles[notificationTypeFilter] : 'All'}
      </Text>
      <DownArrow24 style={listShownIndex === 3 ? Style.inverted : Style.upright} />
    </Pressable>
    <View style={styles.dropdownViewContainer}>
      {listShownIndex === 3 && <View style={styles.dropdownView}>
        {notificationTypes
          ?.filter((item: NotificationType) => happyEnabled || !item
            || [NotificationType.RAISED, NotificationType.UPDATE].includes(item))
          .map((item: NotificationType, index: number) => <Pressable
            key={`notification_type_filter_item_${index}`}
            onPress={() => {
              setNotificationTypeFilter(item);
              if ([NotificationType.RAISED, NotificationType.UPDATE].includes(item)) {
                setSeverityFilter(null);
              } else if ([
                NotificationType.ALERT, NotificationType.ANOMALY, NotificationType.INSIGHT,
              ].includes(item)) {
                setPriorityFilter(null);
                setProgressionFilter(null);
              }
              setListShownIndex(-1);
            }}
            style={({ hovered, pressed }: PressableState) => [
              styles.option,
              hovered && Style.listItemHovered, pressed && Style.listItemPressed,
              item === notificationTypeFilter && styles.itemSelected,
            ]}
            testID={notificationTypeTestIDs[index]}
          >
            <Text style={[styles.value, item === notificationTypeFilter && styles.valueSelected]}>
              {item ? notificationTypeTitles[item] : 'All'}
            </Text>
          </Pressable>)}
      </View>}
    </View>
    {[NotificationType.ALERT, NotificationType.ANOMALY, NotificationType.INSIGHT]
      .includes(notificationTypeFilter) && <>
        <View style={Style.row}>
          <Text
            style={styles.label}
            testID='filter-label'
          >
            {'Severity'}
          </Text>
          {isValidValue(severityFilter, NotificationSeverity)
            && <PressableStyled onPress={() => setSeverityFilter(null)}>
              <Text style={styles.resetFiltersText}>{'RESET'}</Text>
            </PressableStyled>}
        </View>
        <PressableStyled
          style={styles.dropdownButton}
          onPress={() => setListShownIndex(listShownIndex === 2 ? -1 : 2)}
        >
          <Text style={styles.value}>
            {isValidValue(severityFilter, NotificationSeverity)
              ? severityTitles[severityFilter] : severityTitles[0]}
          </Text>
          <DownArrow24 style={listShownIndex === 2 ? Style.inverted : Style.upright} />
        </PressableStyled>
        <View style={styles.dropdownViewContainer}>
          {listShownIndex === 2 && <View style={styles.dropdownView}>
            {severityValues?.map((value: number, index: number) => <Pressable
              key={`severity_filter_item_${index}`}
              testID={`severity#${index}`}
              onPress={() => {
                setSeverityFilter(value);
                setListShownIndex(-1);
              }}
              style={({ hovered, pressed }: PressableState) => [
                styles.option,
                hovered && Style.listItemHovered, pressed && Style.listItemPressed,
                value === severityFilter && styles.itemSelected,
              ]}
            >
              <Text style={[styles.value, value === severityFilter && styles.valueSelected]}>
                {severityTitles[index]}
              </Text>
            </Pressable>)}
          </View>}
        </View>
      </>}
    {(!notificationTypeFilter
      || [NotificationType.RAISED, NotificationType.UPDATE].includes(notificationTypeFilter)) && <>
        <View style={Style.row}>
          <Text
            style={styles.label}
            testID='filter-label'
          >
            {'Priority'}
          </Text>
          {isValidValue(priorityFilter, TicketPriority)
            && <PressableStyled onPress={() => setPriorityFilter(null)}>
              <Text style={styles.resetFiltersText}>{'RESET'}</Text>
            </PressableStyled>}
        </View>
        <Pressable
          style={styles.dropdownButton}
          onPress={() => {
            logEvent('NotificationFilter', 'priority_filter');
            setListShownIndex(listShownIndex === 1 ? -1 : 1);
          }}
          testID='notifications-priority-filter-btn'
        >
          <Text style={styles.value}>
            {isValidValue(priorityFilter, TicketPriority)
              ? `P${priorityFilter + 1} - ${slaNames[priorityFilter]}` : 'All'}
          </Text>
          <DownArrow24 style={listShownIndex === 1 ? Style.inverted : Style.upright} />
        </Pressable>
        <View style={styles.dropdownViewContainer}>
          {listShownIndex === 1 && <View style={styles.dropdownView}>
            {priorityValues?.map((item: TicketPriority, index: number) => <Pressable
              key={`priority_filter_item_${index}`}
              testID={`priority#${index}`}
              onPress={() => {
                setPriorityFilter(item);
                setListShownIndex(-1);
              }}
              style={({ hovered, pressed }: PressableState) => [
                styles.option,
                hovered && Style.listItemHovered, pressed && Style.listItemPressed,
                item === priorityFilter && styles.itemSelected,
              ]}
            >
              <Text style={[styles.value, item === priorityFilter && styles.valueSelected]}>
                {index === 0 ? 'All' : `P${index} - ${slaNames[index - 1]}`}
              </Text>
            </Pressable>)}
          </View>}
        </View>
        <View style={Style.row}>
          <Text
            style={styles.label}
            testID='filter-label'
          >
            {'Progression'}
          </Text>
          {!!progressionFilter && <PressableStyled onPress={() => setProgressionFilter(null)}>
            <Text style={styles.resetFiltersText}>{'RESET'}</Text>
          </PressableStyled>}
        </View>
        <Pressable
          style={styles.dropdownButton}
          onPress={() => {
            logEvent('NotificationFilter', 'progression_filter');
            setListShownIndex(listShownIndex === 4 ? -1 : 4);
          }}
          testID='notifications-progression-filter-btn'
        >
          <Text style={styles.value}>
            {progressionFilter ? progressionTitles[progressionFilter] : 'All'}
          </Text>
          <DownArrow24 style={listShownIndex === 4 ? Style.inverted : Style.upright} />
        </Pressable>
        <View style={styles.dropdownViewContainer}>
          {listShownIndex === 4 && <View style={styles.dropdownView}>
            {progressionValues?.map((item: TicketStatus, index: number) => <Pressable
              key={`progression_filter_item_${index}`}
              onPress={() => {
                setProgressionFilter(item);
                setListShownIndex(-1);
              }}
              style={({ hovered, pressed }: PressableState) => [
                styles.option,
                hovered && Style.listItemHovered, pressed && Style.listItemPressed,
                item === progressionFilter && styles.itemSelected,
              ]}
              testID={progressionTestIDs[index]}
            >
              <Text style={[styles.value, item === progressionFilter && styles.valueSelected]}>
                {item ? progressionTitles[item] : 'All'}
              </Text>
            </Pressable>)}
          </View>}
        </View>
      </>}
    <ResetFiltersButton
      onPress={resetFilters}
      style={styles.resetFiltersButton}
    />
    <View style={styles.buttonContainer}>
      <Button
        disabled={loading}
        title='Apply'
        onPress={() => {
          applyFilter(
            dateFilter,
            priorityFilter,
            notificationTypeFilter,
            progressionFilter,
            severityFilter,
          );
          setListShownIndex(-1);
        }}
        affirmative
        style={{ ...styles.button, ...styles.applyButton }}
        testID='apply-filter-btn'
      />
      <Button
        title='Close'
        onPress={hideFilter}
        style={{ ...styles.button, ...styles.closeButton }}
      />
    </View>
    {isMobile && <>
      <ActivityIndicator
        size='large'
        style={Style.activityIndicator}
        animating={loading}
      />
      <AlertModal
        title={filterAlertTitle}
        message={filterAlertMessage}
        modalVisible={filterAlertVisible}
        setModalVisible={filterAlertToggle}
      />
    </>}
  </Animated.View>;
};

const styles = StyleSheet.create({
  container: {
    width: isMobile ? Dimensions.get('window').width : width,
    position: isMobile ? 'relative' : 'absolute',
    height: '100%',
    zIndex: 5,
    top: 0,
    end: 0,
    backgroundColor: '#FFF',
    borderTopLeftRadius: isMobile ? 10 : undefined,
    borderTopRightRadius: 10,
    borderStartWidth: 1,
    borderStartColor: Colors.light.hereworksBlue100,
    padding: 24,
    paddingTop: isMobile ? 0 : 24,
    overflow: isMobile ? 'scroll' : 'visible',
    maxWidth: 450,
    alignSelf: 'center',
  },
  title: {
    fontSize: isMobile ? 16 : 18,
    lineHeight: isMobile ? 24 : 27,
    fontFamily: isMobile ? 'Poppins_500Medium' : 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginBottom: 24,
  },
  label: {
    marginBottom: 4,
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  datePicker: {
    marginBottom: 12,
    zIndex: 2,
  },
  dropdownButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 4,
  },
  dropdownViewContainer: {
    zIndex: 2,
  },
  dropdownView: {
    borderWidth: 1.5,
    borderRadius: 8,
    borderColor: Colors.light.hereworksBlue100,
    marginBottom: 4,
    overflow: 'hidden',
    position: 'absolute',
    backgroundColor: '#FFF',
    width: '100%',
  },
  value: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  option: {
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  itemSelected: {
    backgroundColor: Colors.light.hereworksDarkBlue,
  },
  valueSelected: {
    color: '#FFF',
  },
  resetFiltersButton: {
    marginTop: isMobile ? 32 : 40,
    marginBottom: 24,
    paddingVertical: 8,
  },
  resetFiltersText: {
    fontSize: 12,
    lineHeight: 14,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack500,
    textDecorationLine: 'underline',
  },
  buttonContainer: {
    flexDirection: isMobile ? 'column-reverse' : 'row',
    width: '100%',
    flex: 1,
  },
  button: {
    height: isMobile ? undefined : 32,
    width: isMobile ? '100%' : undefined,
    paddingHorizontal: 12,
    flex: undefined,
  },
  applyButton: {
    marginStart: 0,
  },
  closeButton: {
    marginStart: isMobile ? 0 : 8,
    marginBottom: isMobile ? 8 : 0,
  },
});

export default NotificationFilter;
